/**
 * Append-only AI session feedback documents for the teacher-feedback app.
 *
 * Stored at:
 * `teacherFeedback/data/courses/{courseId}/mentors/{zoomUserId}/sessions/{sessionKey}/feedback/{feedbackId}`
 *
 * `sessionKey` is Zoom’s session start time in milliseconds (`startTimeMs`), as
 * a string path segment (same id as the parent session stats document).
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../../types';
import {TeacherFeedbackConfigSchema} from './feedbackConfig';

// ============================================================================
// Dynamic feedback payload values
// ============================================================================

/**
 * Timestamp range from the start of the recording, for `fieldType: 'moment'`.
 *
 * Values are strings such as **`HH:MM:SS`** (offsets from recording start),
 * aligned with transcript line timestamps.
 */
export const FeedbackMomentValueSchema = z.object({
  /** Inclusive start of the span (e.g. `HH:MM:SS`). */
  start: z.string(),
  /** Inclusive end of the span (e.g. `HH:MM:SS`). */
  end: z.string(),
});

export type FeedbackMomentValue = z.infer<typeof FeedbackMomentValueSchema>;

/**
 * Per-field value: either a string (`text`) or a moment range (`moment`).
 */
export const FeedbackFieldValueSchema = z.union([
  z.string(),
  FeedbackMomentValueSchema,
]);

export type FeedbackFieldValue = z.infer<typeof FeedbackFieldValueSchema>;

// ============================================================================
// Human evaluation (optional; written after generation)
// ============================================================================

/**
 * Optional human / in-app evaluation attached to a feedback document.
 *
 * Kept minimal so the rubric can evolve in docs/process before the schema
 * changes again. `rating` is a 1–5 scalar; extend when a richer rubric lands.
 */
export const TeacherFeedbackEvaluationSchema = z.object({
  /** Overall usefulness / quality on a 1–5 scale (higher = better). */
  rating: z.number().min(1).max(5).optional(),
  /** Short free-form rationale. */
  notes: z.string().optional(),
  /** When the evaluation was recorded. */
  evaluatedAt: FirestoreTimestampSchema,
  /** Firebase Auth uid of the evaluator. */
  evaluatedBy: z.string(),
});

export type TeacherFeedbackEvaluation = z.infer<
  typeof TeacherFeedbackEvaluationSchema
>;

// ============================================================================
// Session feedback document
// ============================================================================

/**
 * One append-only AI feedback row under
 * `…/sessions/{sessionKey}/feedback/{feedbackId}`.
 *
 * Old rows stay readable after config changes because `configSnapshot` and
 * `feedback` reflect the version that produced this document.
 */
export const TeacherFeedbackSessionFeedbackSchema = z.object({
  /**
   * Structured LLM output: keys match `FeedbackOutputField.key` from the
   * config used at generation; values are strings (`text`) or moment objects.
   */
  feedback: z.record(z.string(), FeedbackFieldValueSchema),
  /** Id of the version doc under `feedbackConfig/{configId}`. */
  configId: z.string(),
  /**
   * Full config as of generation time, so the client can render without
   * loading `versions/{configId}`.
   */
  configSnapshot: TeacherFeedbackConfigSchema,
  /** When this feedback was generated (server time). */
  generatedAt: FirestoreTimestampSchema,
  /** Model id actually used for this completion (may differ from config default). */
  model: z.string(),
  /** Owning course (`teacherFeedback/data/courses/{courseId}`). */
  courseId: z.string(),
  /** Zoom host user id for this mentor (`…/mentors/{zoomUserId}`). */
  zoomUserId: z.string(),
  /**
   * Zoom session start time in milliseconds (`startTimeMs`), stringified for the
   * parent stats doc id and path segment (`…/sessions/{sessionKey}`).
   */
  sessionKey: z.string(),
  /**
   * Optional human / in-app evaluation for this generation. Written by
   * teachers or admins via a client-side merge update; generation itself
   * remains server-only.
   */
  evaluation: TeacherFeedbackEvaluationSchema.optional(),
});

export type TeacherFeedbackSessionFeedback = z.infer<
  typeof TeacherFeedbackSessionFeedbackSchema
>;

// ============================================================================
// Paths
// ============================================================================

function sessionDocumentRoot({
  courseId,
  zoomUserId,
  sessionKey,
}: {
  courseId: string;
  zoomUserId: string;
  sessionKey: string;
}): string {
  return `teacherFeedback/data/courses/${courseId}/mentors/${zoomUserId}/sessions/${sessionKey}`;
}

/**
 * Feedback subcollection for a mentor session.
 * `.../mentors/{zoomUserId}/sessions/{sessionKey}/feedback`
 *
 * `sessionKey` — Zoom `startTimeMs` as a string path segment.
 */
export function getTeacherFeedbackSessionFeedbackCollectionPath({
  courseId,
  zoomUserId,
  sessionKey,
}: {
  courseId: string;
  zoomUserId: string;
  sessionKey: string;
}): string {
  return `${sessionDocumentRoot({courseId, zoomUserId, sessionKey})}/feedback`;
}

/**
 * A single feedback document path.
 */
export function getTeacherFeedbackSessionFeedbackDocumentPath({
  courseId,
  zoomUserId,
  sessionKey,
  feedbackId,
}: {
  courseId: string;
  zoomUserId: string;
  sessionKey: string;
  feedbackId: string;
}): string {
  return `${getTeacherFeedbackSessionFeedbackCollectionPath({
    courseId,
    zoomUserId,
    sessionKey,
  })}/${feedbackId}`;
}
